import { useState, useEffect, useContext } from 'react';
import {
  DataGrid,
  getGridStringOperators,
  GridToolbarQuickFilter,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { Box } from '@mui/material';
import ToastContext from '../store/toast-context';
import { useHistory } from 'react-router-dom';
import { useLoaded } from 'src/store/load-context';
import { formatDateTime } from '@itdeptutahcountygovernment/date-formatting';
import useAdminAPI from 'src/services/useAdminAPI';

function ToolBar() {
  return (
    <Box
      sx={{
        p: 0.5,
        pb: 0,
      }}
    >
      <GridToolbarContainer>
        <GridToolbarQuickFilter
          autoFocus
          debounceMs={900}
          inputProps={{ maxLength: 7 }}
          placeholder="Account Number"
          sx={{ mr: 'auto' }}
        />
        <GridToolbarFilterButton />
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    </Box>
  );
}

export default function WebFilingDatatable() {
  const api = useAdminAPI();
  const history = useHistory();
  const { loaded, setLoaded } = useLoaded();
  const { handleError } = useContext(ToastContext);
  const [rows, setRows] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [paginationModel, setpaginationModel] = useState({
    rowCount: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState([
    {
      field: 'taxYear',
      sort: 'desc',
    },
  ]);

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains'].includes(value)
  );

  useEffect(() => {
    setLoaded(false);

    const fetchBusinessFilings = async () => {
      try {
        const res = await api.get('/admin/filings', {
          params: {
            paginationModel,
            filterModel,
            sortModel,
          },
        });

        setLoaded(true);
        return res.data;
      } catch (err) {
        console.error(err);
        handleError(err);
      }
    };

    const updateRows = async () => {
      const data = await fetchBusinessFilings();
      if (data) {
        const { businessFilings, count } = data;
        const newRows = createRows(businessFilings);
        setpaginationModel({ ...paginationModel, rowCount: count });
        setRows(newRows);
      }
    };

    updateRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.pageSize, paginationModel.page, filterModel, sortModel]);

  const createRows = (businessesFilingData) =>
    businessesFilingData.map(
      ({
        accountNo,
        taxYear,
        busName,
        signed,
        importDate,
        bypassItemization,
        reqExempt,
        equipment,
        total,
        busInOper,
        remarks,
      }) => ({
        accountNo,
        taxYear,
        busName,
        signed,
        importDate: formatDateTime(importDate),
        bypassItemization,
        reqExempt,
        equipment,
        total,
        busInOper,
        remarks,
      })
    );

  const columns = [
    { field: 'accountNo', headerName: 'Account #', filterOperators, width: 115 },
    { field: 'taxYear', headerName: 'Tax Year', filterOperators, width: 115 },
    { field: 'busName', headerName: 'Business Name', filterOperators, width: 200 },
    { field: 'signed', headerName: 'Signed', filterOperators, width: 100 },
    { field: 'importDate', headerName: 'Date Filed', filterOperators, width: 200 },
    { field: 'bypassItemization', headerName: 'Bypass', filterOperators, width: 100 },
    { field: 'reqExempt', headerName: 'Req Exempt', filterOperators, width: 130 },
    { field: 'equipment', headerName: 'Equipment', filterOperators, width: 130 },
    { field: 'total', headerName: 'Total', filterOperators, width: 100 },
    { field: 'busInOper', headerName: 'In Op', filterOperators, width: 100 },
    { field: 'remarks', headerName: 'Remarks', filterOperators, width: 500 },
  ];

  const handlePageChange = (newPage) => {
    setpaginationModel({ ...paginationModel, page: newPage });
  };

  const handleSizeChange = (newPageSize) => {
    setpaginationModel({ ...paginationModel, pageSize: newPageSize });
  };

  const handleFilterChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const handleSortModelChange = (newSort) => {
    setSortModel(newSort);
  };

  const handleRowClick = ({ row }) => {
    history.push(`/admin/account/details?accountNo=${row.accountNo}&taxYear=${row.taxYear}`);
  };

  return (
    <Box sx={{ bgcolor: 'white', width: '80vw' }}>
      {/* https://github.com/mui/mui-x/issues/4076 */}
      <DataGrid
        sx={{
          '.MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel': {
            my: '1rem',
          },
        }}
        density="compact"
        autoHeight
        rowHeight={50}
        pagination
        paginationMode="server"
        loading={!loaded}
        rowCount={paginationModel.rowCount}
        rowsPerPageOptions={paginationModel.rowsPerPageOptions}
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        rows={rows}
        columns={columns}
        onPageChange={handlePageChange}
        onPageSizeChange={handleSizeChange}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleFilterChange}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        onRowClick={handleRowClick}
        getRowId={(row) => `${row.accountNo}-${row.taxYear}`}
        components={{ Toolbar: ToolBar }}
        disableColumnMenu
      />
    </Box>
  );
}
